import type { AxiosError } from 'axios';
import axios from 'axios';

import type { PayloadAction } from '@reduxjs/toolkit';
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import type { GroupedByMonth } from '../../components/Calendar/calendar.service';
import { formatByMonths, getSelectedMonth, saveSelectedMonth } from '../../components/Calendar/calendar.service';
import { getIsOnline } from '../../helpers/utils.helper';
import type { EnglishTranslation } from '../../i18n';
import type { ErrorMessage } from '../../models/error';
import type { AsyncThunkConfig } from '../../models/slice';
import { RaygunErrorHandlerService } from '../../services/raygun.service';

const { logError } = RaygunErrorHandlerService();

type EntriesSliceType = {
  entries: GroupedByMonth<EntrySubmission>[];
  selectedMonthEntries: GroupedByMonth<EntrySubmission> | undefined;
};

export type EntrySubmission = {
  submission_id: string;
  client_id: string;
  mood_alias: keyof EnglishTranslation['moods'];
  diary: string;
  mood_text: string | null;
  short_name: string;
  name: string;
  created_date: string;
  timestamp: string;
  fields: Field[];
  offline?: boolean;
};

type Field = {
  field_name_alias: keyof EnglishTranslation['fields'];
  response_alias: keyof EnglishTranslation['fields'];
  response_value: string;
};

export const fetchEntries = createAsyncThunk<GroupedByMonth<EntrySubmission>[], undefined, AsyncThunkConfig>(
  'entries/fetchEntries',
  async (_, thunkAPI) => {
    try {
      const online = await getIsOnline();
      let entries: EntrySubmission[] = [];
      if (online) {
        entries = await axios.post('/v3_submission');
      }

      //offline journaling
      const { getSavedJournals } = await import('../../services/offlineJournal.service');
      const journals = (await getSavedJournals()) as unknown as EntrySubmission[];
      if (journals.length > 0) {
        entries.unshift(...journals);
      }

      return formatByMonths(entries);
    } catch (e) {
      logError(e, ['entriesSlice', 'fetchEntries']);
      return thunkAPI.rejectWithValue((e as AxiosError<ErrorMessage>).response?.data);
    }
  },
);

const initialState: EntriesSliceType = {
  entries: [],
  selectedMonthEntries: undefined,
};

export const entriesSlice = createSlice({
  name: 'entries',
  initialState,
  reducers: {
    updateSelectedMonth(state, action: PayloadAction<string>) {
      saveSelectedMonth(action.payload);
      state.selectedMonthEntries = state.entries.find((e) => e.groupName === action.payload);
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchEntries.fulfilled, (state, action) => {
      state.entries = action.payload;
      state.selectedMonthEntries = action.payload.find((e) => e.groupName === getSelectedMonth()) ?? action.payload[0];
    });
  },
});

export const { updateSelectedMonth } = entriesSlice.actions;
